import {
  ItemTypeConsts,
  ItemTypeRoutes,
  LoginModalConsts,
  OnboardingChecklistConsts,
} from 'client/util/Constants';
import {
  assign,
  camelCase,
  cloneDeep,
  get,
  isEmpty,
  merge,
  values,
} from 'lodash';
import { tasks as checklistTasks, implementationChecklists } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import {
  defaultOnboardingFeatures,
  implementationChecklistTasks,
  onboardingFeatures,
} from 'client/GrowthEngineering/utilities/GrowthConstants';

import Cookies from 'js-cookie';
import PolicyApi from 'client/api/v2/PolicyApi';
import ProductTourSteps from 'client/components/ProductTour/ProductTourSteps';
import SystemApi from 'client/api/v2/SystemApi';
import UserApi from 'api/v2/UserApi';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import Vue from 'vue';

const { inProgressKey } = OnboardingChecklistConsts;
const implementationChecklistTasksUrls = new Map();
const {
  excludeModalTestingCookie,
} = LoginModalConsts;

Object.values(checklistTasks).forEach((task) => {
  if (task.taskType && task.defaultUrl) {
    implementationChecklistTasksUrls.set(task.taskType, task.defaultUrl);
  }
});

const defaultResourcesDisplayPreferences = {
  resourceTypeFilter: onboardingFeatures.deviceManagement,
  blog: { expanded: false, visible: true },
  community: { expanded: false, visible: true },
  guidedSetup: { expanded: false, visible: true },
  msp: { expanded: false, visible: true },
  tutorialResources: { expanded: true, visible: true },
};

const defaultOnboardingState = {
  chatSeen: false,
  checklistExpandedIndex: 1,
  directOnboardingFeature: '',
  implementationChecklistCompletedTasks: {},
  implementationChecklistDismissed: true,
  implementationChecklists: [],
  newFeatureItemCount: 0,
  newFeaturesSeen: {},
  onboardingModalSeen: false,
  onboardingTasks: {},
  recommendedChecklists: {},
  resourcesDisplayPreferences: defaultResourcesDisplayPreferences,
  simplifiedOnboardingDismissed: false,
  selectedJob: null,
  showOnboarding: false,
  taskChecklistComplete: false,
};

const defaultAdminGrowthData = {
  onboardingState: defaultOnboardingState,
  experimentStates: {},
};

// --------------------------------------------------------------------------
// Private functions
// --------------------------------------------------------------------------
const getTaskStorageKey = task => `jcOnboardingChecklistTask-${camelCase(task.title)}`;

const putAdminGrowthData = payload => UserApi.putSelf(payload);

const updateGrowthDataOnboardingState = (state, updateObject) => {
  const currentOnboardingState = get(state, 'adminGrowthData.onboardingState', {});
  const newOnboardingState = {
    onboardingState: {
      ...currentOnboardingState,
      ...updateObject,
    },
  };
  const newGrowthData = assign({}, state.adminGrowthData, newOnboardingState);

  return newGrowthData;
};

// --------------------------------------------------------------------------
// Actions
// --------------------------------------------------------------------------
function addFeatureToImplementationChecklist({ commit, state }, feature) {
  const implementationChecklistCompletedTasks = get(state, 'adminGrowthData.onboardingState.implementationChecklistCompletedTasks', {});

  feature.tasks.forEach((checklistTask) => {
    // eslint-disable-next-line no-param-reassign
    checklistTask.complete = !!implementationChecklistCompletedTasks[checklistTask.taskType];
  });
  commit('ADD_FEATURE_TO_IMPLEMENTATION_CHECKLIST', feature);
}

function addFeatureToRecommendedChecklist({ commit, state }, feature) {
  const implementationChecklistCompletedTasks = get(state, 'adminGrowthData.onboardingState.implementationChecklistCompletedTasks', {});

  feature.tasks.forEach((checklistTask) => {
    // eslint-disable-next-line no-param-reassign
    checklistTask.complete = !!implementationChecklistCompletedTasks[checklistTask.taskType];
  });
  commit('ADD_FEATURE_TO_RECOMMENDED_CHECKLIST', feature);
}

function addImplementationChecklists({ commit }, checklists) {
  commit('SET_IMPLEMENTATION_CHECKLISTS', checklists);
}

function addOnboardingTasks({ commit, dispatch, state }, { tasks, checkLocalStorage = true }) {
  const { adminGrowthData } = state;
  const { onboardingState } = adminGrowthData;
  tasks.forEach((task) => {
    // Check that the task is not already in the store
    if (!Object.prototype.hasOwnProperty.call(state.onboardingTasks, task.title)) {
      let completed = false;

      // check local storage to see if task has already been completed
      if (checkLocalStorage) {
        const taskStorageKey = getTaskStorageKey(task);
        const growthObjectTask = (onboardingState.onboardingTasks || {})[task.title];

        completed = growthObjectTask || !!localStorage.getItem(taskStorageKey);

        commit('SET_ONBOARDING_TASK_STATUS', { task, completed });
      } else {
        try {
          task.checkIfComplete()
            .then(result => commit('SET_ONBOARDING_TASK_STATUS', { task, completed: result }));
        } catch (err) {
          completed = false;
          commit('SET_ONBOARDING_TASK_STATUS', { task, completed });
        }
      }
    }
  });
  dispatch('updateOnboardingChecklistStatus');
  putAdminGrowthData({ growthData: state.adminGrowthData });
}

function clearNewFeatureAlert({ commit, dispatch, state }, feature) {
  commit('CLEAR_NEW_FEATURE_ALERT', feature);
  const { adminGrowthData } = state;
  const { onboardingState } = adminGrowthData;
  const updatedFeatureCount = onboardingState.newFeatureItemCount - +1;
  dispatch('updateNewFeatureItemCount', updatedFeatureCount);
}

function completeOnboardingChecklist({ commit }) {
  commit('SET_ONBOARDING_CHECKLIST_COMPLETE', true);
}

function completeOnboardingTask({ commit, dispatch, state }, task) {
  // Only complete task if incomplete
  if (state.onboardingTasks[task.title] === false) {
    // update store
    commit('SET_ONBOARDING_TASK_STATUS', { task, completed: true });

    // update local storage
    const taskStorageKey = getTaskStorageKey(task);
    localStorage.setItem(taskStorageKey, 'complete');
    localStorage.setItem(inProgressKey, 'inProgress');
    dispatch('updateOnboardingChecklistStatus');
    putAdminGrowthData({ growthData: state.adminGrowthData });
  }
}

function removeFeatureFromImplementationChecklist({ commit }, feature) {
  commit('REMOVE_FEATURE_FROM_IMPLEMENTATION_CHECKLIST', feature);
}

function setAdminGrowthData(context, { growthData, pushChanges = true }) {
  context.commit('SET_ADMIN_GROWTH_DATA', { growthData });

  if (pushChanges) {
    putAdminGrowthData({ growthData: context.state.adminGrowthData });
  }
}

function setInitialChecklistFeatures({ commit }, initalCheckListFeatures) {
  commit('SET_INITIAL_CHECKLIST_FEATURES', initalCheckListFeatures);
}

function setProductTourStep({ commit }, productTourStepIndex) {
  commit('SET_PRODUCT_TOUR_STEP', productTourStepIndex);
}

function setShowChecklistLoading({ commit }, showChecklistLoading) {
  commit('SET_SHOW_CHECKLIST_LOADING', showChecklistLoading);
}

function setShowOnboarding({ commit, rootGetters, getters }) {
  const hasActiveCrowdStrikeLink = rootGetters['PartnersModule/hasActiveCrowdStrikeLink'];
  const hasPendingCrowdStrikeTrial = rootGetters['PartnersModule/hasPendingCrowdStrikeTrial'];
  const isCommandRunner = rootGetters['SettingsModule/isCommandRunner'];
  const excludeTesting = !!Cookies.get(excludeModalTestingCookie);
  const onboardingFlowComplete = getters.getOnboardingFlowComplete;
  const orgCreatedDate = rootGetters['OrganizationsCollection/getOrgCreatedDate'];
  const orgIsOlderThanDays = rootGetters['OrganizationsCollection/getOrgIsOlderThanDays'];

  const show = orgCreatedDate
    && !isCommandRunner
    && !orgIsOlderThanDays(10)
    && !onboardingFlowComplete
    && !excludeTesting
    && !(hasActiveCrowdStrikeLink || hasPendingCrowdStrikeTrial);

  commit('SET_SHOW_ONBOARDING', show);
}

function setShowProductTour({ commit }, showProductTour) {
  commit('SET_SHOW_PRODUCT_TOUR', showProductTour);
}

function updateChatSeen({ commit, state }, seen) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { chatSeen: seen })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  commit('SET_CHAT_SEEN', seen);
}

function updateChecklistExpandedIndex({ commit, state }, index) {
  commit('SET_CHECKLIST_EXPANDED_INDEX', index);
  putAdminGrowthData({ growthData: state.adminGrowthData });
}

function updateDirectOnboardingFeature({ commit, state }, feature) {
  commit('SET_DIRECT_ONBOARDING_FEATURE', feature);
  putAdminGrowthData({ growthData: state.adminGrowthData });
}

function updateImplementationChecklist({ commit }, feature) {
  commit('UPDATE_IMPLEMENTATION_CHECKLIST', { feature });
}

function updateImplementationChecklistDismissed({ commit, state }, dismissed) {
  commit('SET_IMPLEMENTATION_CHECKLIST_DISMISSED', dismissed);
  putAdminGrowthData({ growthData: state.adminGrowthData });
}

function updateImplementationTaskStatus({ commit }, {
  checklist, taskIndex, taskType, complete,
}) {
  commit('SET_CHECKLIST_COMPLETED_TASK', { complete: true, taskType });
  commit('SET_IMPLEMENTATION_CHECKLIST_TASK_STATUS', {
    checklist, taskIndex, complete,
  });
}

function updateImplementationTaskStatusAuto({ commit, getters }, { taskType }) {
  commit('SET_CHECKLIST_COMPLETED_TASK', { complete: true, taskType });
  const checklists = getters.getImplementationChecklist;
  checklists.forEach(checklist => {
    const taskIndex = checklist.tasks.findIndex(task => task.taskType === taskType
      && !task.complete);

    if (taskIndex > -1) {
      commit('SET_IMPLEMENTATION_CHECKLIST_TASK_STATUS', {
        checklist, taskIndex, complete: true,
      });
    }
  });
}

function updateNewFeatureItemCount({ commit, state }, count) {
  commit('SET_NEW_FEATURE_ITEM_COUNT', count);
  putAdminGrowthData({ growthData: state.adminGrowthData });
}

function updateOnboardingChecklistStatus({ dispatch, state }) {
  const taskStatusList = values(state.onboardingTasks);

  if (!state.taskChecklistComplete
    && taskStatusList.length
    && taskStatusList.every(completionStatus => completionStatus)) {
    dispatch('completeOnboardingChecklist');
  }
}

function updateOnboardingFlowComplete({ commit, dispatch, state }) {
  const onboardingState = get(state, 'adminGrowthData.onboardingState', {});

  const {
    onboardingModalSeen,
    simplifiedOnboardingDismissed,
  } = onboardingState;

  commit('SET_SHOW_ONBOARDING', false);
  if (!onboardingModalSeen || !simplifiedOnboardingDismissed) {
    dispatch('updateSimplifiedOnboardingDismissed');
    dispatch('updateOnboardingModalSeen');
  }
}

function updateOnboardingModalSeen({ commit, state }) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { onboardingModalSeen: true })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  commit('SET_ONBOARDING_MODAL_SEEN', true);
}

function updateResourcesDisplayPreferences({ commit }, {
  resourcesDisplayPreferences,
}) {
  commit('SET_RESOURCES_VISIBILITY', { resourcesDisplayPreferences });
}

function updateSimplifiedOnboardingDismissed({ commit, state }) {
  commit('SET_SIMPLIFIED_ONBOARDING_DISMISSED', true);
  putAdminGrowthData({ growthData: state.adminGrowthData });
}

async function updateTaskUrl({ commit, dispatch }, { taskType }) {
  let url;

  switch (taskType) {
    case implementationChecklistTasks.bindUserToUserGroup: {
      const userGroupsData = await UserGroupApi.fetch(0, 2);
      const userGroups = userGroupsData.results;

      if (userGroups && userGroups.length) {
        const filteredUserGroups = userGroups.filter(userGroup => userGroup.name !== 'All Users');
        const allUsersGroups = userGroups.filter(userGroup => userGroup.name === 'All Users');

        if (filteredUserGroups.length) {
          const firstGroupId = filteredUserGroups[0].id;
          url = `${ItemTypeRoutes[ItemTypeConsts.userGroup]}/${firstGroupId}/details/users`;
        } else if (allUsersGroups.length) {
          const allUsersGroupId = allUsersGroups[0].id;
          url = `${ItemTypeRoutes[ItemTypeConsts.userGroup]}/${allUsersGroupId}/details/users`;
        }
      }
      break;
    }
    case implementationChecklistTasks.bindUserToDevice: {
      const systemsData = await SystemApi.fetch(0, 1);
      const systems = systemsData.results;

      if (systems && systems.length) {
        const firstSystemId = systems[0].id;
        url = `${ItemTypeRoutes[ItemTypeConsts.device]}/${firstSystemId}/details/users`;
      }
      break;
    }
    case implementationChecklistTasks.bindPolicyToDevice: {
      const policiesData = await PolicyApi.fetch(0, 1);
      const policies = policiesData.results;

      if (policies && policies.length) {
        const firstPolicyId = policies[0].id;
        url = `${ItemTypeRoutes[ItemTypeConsts.configuration]}/${firstPolicyId}/details/devices`;
      }
      break;
    }
    case implementationChecklistTasks.navigateToPasswordManager:
      dispatch('updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.navigateToPasswordManager });
      break;
    case implementationChecklistTasks.observeSystemInsights: {
      const systemsData = await SystemApi.fetch(0, 1);
      const systems = systemsData.results;

      if (systems && systems.length) {
        const firstSystemId = systems[0].id;
        url = `${ItemTypeRoutes[ItemTypeConsts.device]}/${firstSystemId}/details`;
        dispatch('updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.observeSystemInsights });
      }
      break;
    }
    case implementationChecklistTasks.observeDirectoryInsights:
      dispatch('updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.observeDirectoryInsights });
      break;
    case implementationChecklistTasks.viewCloudInsights:
      dispatch('updateImplementationTaskStatusAuto', { taskType: implementationChecklistTasks.viewCloudInsights });
      break;
    default:
      break;
  }

  if (url) {
    commit('SET_TASK_URL', { taskType, url });
  }
}

// --------------------------------------------------------------------------
// Getters
// --------------------------------------------------------------------------
function getChecklistExpandedIndex(state) {
  const onboardingState = get(state, 'adminGrowthData.onboardingState', {});

  const { checklistExpandedIndex } = onboardingState;

  return checklistExpandedIndex;
}

function getDirectOnboardingFeature(state) {
  const onboardingState = get(state, 'adminGrowthData.onboardingState', {});

  const { directOnboardingFeature } = onboardingState;

  return directOnboardingFeature;
}

function getImplementationChecklist(state) {
  const checklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);

  return cloneDeep(checklists);
}

function getImplementationChecklistDismissed(state) {
  const onboardingState = get(state, 'adminGrowthData.onboardingState', {});

  const { implementationChecklistDismissed } = onboardingState;

  return implementationChecklistDismissed;
}

function getImplementationChecklistFeaturesComplete(state) {
  const checklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);
  const implementationChecklistCompletedTasks = get(state, 'adminGrowthData.onboardingState.implementationChecklistCompletedTasks', []);

  const completedChecklists = [];
  checklists.forEach((checklist) => {
    if (checklist.tasks.every(task => implementationChecklistCompletedTasks[task.taskType])) {
      completedChecklists.push(checklist.feature);
    }
  });

  return completedChecklists;
}

function getImplementationChecklistTotal(state) {
  const checklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);
  let totalCount = 1;
  checklists.forEach((checklist) => {
    totalCount += checklist.tasks.length;
  });

  return totalCount;
}

function getImplementationChecklistTotalComplete(state) {
  const checklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);
  let completeCount = 1;
  checklists.forEach((checklist) => {
    completeCount += checklist.tasks.reduce(
      (total, task) => (task.complete ? total + 1 : total), 0,
    );
  });

  return completeCount;
}

function getOnboardingFlowComplete(state) {
  const onboardingState = get(state, 'adminGrowthData.onboardingState', {});

  const {
    onboardingModalSeen,
    simplifiedOnboardingDismissed,
  } = onboardingState;

  return onboardingModalSeen || simplifiedOnboardingDismissed || false;
}

function getProductTourActiveLink(state) {
  const index = get(state, 'productTourStepIndex', 0);

  return ProductTourSteps[index].navPanelLink;
}

function getResourcesDisplayPreferences(state) {
  const experimentId = 20790970295;
  const experimentStates = get(state, 'adminGrowthData.experimentStates', {});
  const onboardingState = get(state, 'adminGrowthData.onboardingState', {});
  const { resourcesDisplayPreferences = {} } = onboardingState;
  const experimentResourceDisplayPreferences = get(experimentStates, `${experimentId}.resourcesDisplayPreferences`, {});
  const existingPrefs = (!isEmpty(resourcesDisplayPreferences) && resourcesDisplayPreferences)
    || (!isEmpty(experimentResourceDisplayPreferences) && experimentResourceDisplayPreferences);

  // ensure default values are filled in if they are missing from object
  return { ...defaultResourcesDisplayPreferences, ...(existingPrefs || {}) };
}

function getResourceFromDisplayPreferences(state, getters) {
  const resourcesDisplayPreferences = getters.getResourcesDisplayPreferences;

  return (resource) => get(resourcesDisplayPreferences,
    resource,
    defaultResourcesDisplayPreferences[resource]);
}

function getResourceTypeFilterFromDisplayPreferences(state, getters) {
  const resourcesDisplayPreferences = getters.getResourcesDisplayPreferences;

  return get(resourcesDisplayPreferences,
    'resourceTypeFilter',
    onboardingFeatures.deviceManagement);
}

function getShowOnboarding(state) {
  const onboardingState = get(state, 'adminGrowthData.onboardingState', {});

  const {
    showOnboarding,
  } = onboardingState;

  return showOnboarding || false;
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------
/**
 * Adds a feature to the implementation checklist
 * @param {Object} feature
 */
function ADD_FEATURE_TO_IMPLEMENTATION_CHECKLIST(state, feature) {
  const checklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);
  const recommendedChecklists = get(state, 'adminGrowthData.onboardingState.recommendedChecklists', {});

  // initalize checklist with default feature
  if (!checklists.length) {
    checklists.push(implementationChecklists[defaultOnboardingFeatures.createAdminAccount]);
  }

  // add new features to the beginning of the checklist (after the default feature)
  if (!checklists.some(f => f.feature === feature.feature)) {
    if (feature.newFeature && checklists.length > 1) {
      checklists.splice(1, 0, feature);
    } else {
      checklists.push(feature);
    }
  }

  // remove feature from recommended next steps if it is there
  if (recommendedChecklists[feature.feature]) {
    delete recommendedChecklists[feature.feature];
  }

  const newGrowthData = (
    updateGrowthDataOnboardingState(state,
      { implementationChecklists: checklists, recommendedChecklists })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Adds a feature to the recommended implementation checklist
 * This checklist does not count towards overall implementation checklist completion,
 * feature chip selection, etc.
 * @param {Object} feature
 */
function ADD_FEATURE_TO_RECOMMENDED_CHECKLIST(state, feature) {
  const checklists = get(state, 'adminGrowthData.onboardingState.recommendedChecklists', {});
  checklists[feature.feature] = feature;

  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { recommendedChecklists: checklists })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Clears a new feature alert from the implementation checklist
 * @param {String} feature
 */
function CLEAR_NEW_FEATURE_ALERT(state, feature) {
  const currentChecklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);
  const newFeaturesSeen = get(state, 'adminGrowthData.onboardingState.newFeaturesSeen', {});
  const index = currentChecklists.findIndex((checklist) => checklist.feature === feature);

  currentChecklists[index].newFeature = false;
  newFeaturesSeen[feature] = true;

  const newGrowthData = (
    updateGrowthDataOnboardingState(state, {
      implementationChecklists: currentChecklists,
    })
  );

  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Removes a feature from the implementation checklist
 * @param {Object} feature
 */
function REMOVE_FEATURE_FROM_IMPLEMENTATION_CHECKLIST(state, feature) {
  const checklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);
  const index = checklists.findIndex(checklist => checklist.feature === feature.feature);

  checklists.splice(index, 1);

  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { implementationChecklists: checklists })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Sets the growthData object to the state
 * @param {Object} growthData - the admin growthData object
 */
function SET_ADMIN_GROWTH_DATA(state, { growthData, maintainPreviousObject = true }) {
  if (maintainPreviousObject) {
    const currentAdminGrowthData = cloneDeep(state.adminGrowthData);
    const currentOnboardingState = get(currentAdminGrowthData, 'onboardingState', defaultOnboardingState);
    const currentResourcesDisplayPreferences = get(currentOnboardingState, 'resourcesDisplayPreferences', defaultResourcesDisplayPreferences);

    const onboardingState = get(growthData, 'onboardingState', {});
    const resourcesDisplayPreferences = get(onboardingState, 'resourcesDisplayPreferences', {});

    const newResourcesDisplayPreferences = merge({},
      currentResourcesDisplayPreferences,
      resourcesDisplayPreferences);

    const newOnboardingState = merge({},
      currentOnboardingState,
      onboardingState,
      { resourcesDisplayPreferences: newResourcesDisplayPreferences });

    const newGrowthObject = merge({},
      currentAdminGrowthData, growthData,
      { onboardingState: newOnboardingState });

    Vue.set(state, 'adminGrowthData', newGrowthObject);
  } else {
    Vue.set(state, 'adminGrowthData', growthData);
  }
}

function SET_CHAT_SEEN(state, chatSeen) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { chatSeen })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

function SET_CHECKLIST_COMPLETED_TASK(state, { complete, taskType }) {
  const implementationChecklistCompletedTasks = get(state, 'adminGrowthData.onboardingState.implementationChecklistCompletedTasks', {});

  implementationChecklistCompletedTasks[taskType] = complete;

  const newGrowthData = (
    updateGrowthDataOnboardingState(state, {
      implementationChecklistCompletedTasks,
    })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * @description Adds checklist expanded index
 * @param {Number} checklistExpandedIndex - A number tracking checklist item expanded index
 */
function SET_CHECKLIST_EXPANDED_INDEX(state, checklistExpandedIndex) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { checklistExpandedIndex })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * @description Adds the feature that is being directly onboarded
 * @param {String} feature - An onboarding feature
 */
function SET_DIRECT_ONBOARDING_FEATURE(state, feature) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { directOnboardingFeature: feature })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * @description Adds whether implementation checklist was dismissed
 * @param {Boolean} implementationChecklistDismissed - A boolean tracking checklist dismissal
 */
function SET_IMPLEMENTATION_CHECKLIST_DISMISSED(state, implementationChecklistDismissed) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { implementationChecklistDismissed })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Updates the status of a task on the implementation checklist
 * @param {Object} checklist - the checklist the task is a part of
 * @param {Number} taskIndex - the index of the task to update
 * @param {Boolean} complete - the status of the task
 */
function SET_IMPLEMENTATION_CHECKLIST_TASK_STATUS(state, { checklist, taskIndex, complete }) {
  const currentChecklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);

  const updatedChecklists = currentChecklists.map((item) => {
    if (item.feature === checklist.feature) {
      const updatedTask = {
        ...item.tasks[taskIndex],
        complete,
      };
      item.tasks.splice(taskIndex, 1, updatedTask);
    }
    return item;
  });

  const newGrowthData = (
    updateGrowthDataOnboardingState(state, {
      implementationChecklists: updatedChecklists,
    })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Sets the implementation checklist based on features selected in onboarding
 * @param {Array} checklists - the checklists to add
 */
function SET_IMPLEMENTATION_CHECKLISTS(state, checklists) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { implementationChecklists: checklists })
  );
  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * @description Adds the inital state of selected features from onboarding
 * @param {Array} initalCheckListFeatures - The array list of features
 */
function SET_INITIAL_CHECKLIST_FEATURES(state, initalCheckListFeatures) {
  Vue.set(state, 'initalCheckListFeatures', initalCheckListFeatures);
}

/**
 * Sets the new feature item count
 * @param {Number} count
 */
function SET_NEW_FEATURE_ITEM_COUNT(state, count) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { newFeatureItemCount: count })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Adds tasks to the state to keep track of
 * @param {Boolean} taskChecklistComplete - A boolean tracking checklist completion
 */
function SET_ONBOARDING_CHECKLIST_COMPLETE(state, completed) {
  Vue.set(state, 'taskChecklistComplete', completed);
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { taskChecklistComplete: completed })
  );

  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
  * Adds onboarding modal seen status to the state to keep track of
  * @param {Boolean} onboardingModalSeen - A boolean tracking whether the modal has appeared
  */
function SET_ONBOARDING_MODAL_SEEN(state, onboardingModalSeen) {
  Vue.set(state, 'onboardingModalSeen', onboardingModalSeen);
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { onboardingModalSeen })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Adds tasks to the state to keep track of
 * @param {Array} tasks - An array of task objects (e.g. [{ title: 'Task' }])
 */
function SET_ONBOARDING_TASK_STATUS(state, { task, completed }) {
  Vue.set(state.onboardingTasks, task.title, completed);
  const currentTasks = get(state, 'adminGrowthData.onboardingState.onboardingTasks', {});
  const newTaskState = {
    onboardingState: {
      ...state.adminGrowthData.onboardingState,
      onboardingTasks: {
        ...currentTasks,
        [task.title]: completed,
      },
    },
  };
  const newGrowthData = merge({}, state.adminGrowthData, newTaskState);
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Sets the current step of the Product Tour
 * @param {Number} productTourStepIndex - current index of Product Tour step from ProductTourSteps
 */
function SET_PRODUCT_TOUR_STEP(state, productTourStepIndex) {
  Vue.set(state, 'productTourStepIndex', productTourStepIndex);
}

function SET_RESOURCES_VISIBILITY(state, { resourcesDisplayPreferences }) {
  let currentResources;

  if (state.adminGrowthData.onboardingState.resourcesDisplayPreferences) {
    currentResources = get(state, 'adminGrowthData.onboardingState.resourcesDisplayPreferences', {});
  } else {
    const experimentId = 20790970295;
    const experimentState = cloneDeep(state.adminGrowthData.experimentStates[experimentId]);
    currentResources = get(experimentState, 'resourcesDisplayPreferences', {});
  }

  const newOnboardingState = {
    onboardingState: {
      ...state.adminGrowthData.onboardingState,
      resourcesDisplayPreferences: {
        ...currentResources,
        ...resourcesDisplayPreferences,
      },
    },
  };

  const newGrowthData = merge({}, state.adminGrowthData, newOnboardingState);
  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Sets the Checklist loading animation to play
 * @param {Boolean} showChecklistLoading - set visibility to true/false
 */
function SET_SHOW_CHECKLIST_LOADING(state, showChecklistLoading) {
  Vue.set(state, 'showChecklistLoading', showChecklistLoading);
}

function SET_SHOW_ONBOARDING(state, showOnboarding) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { showOnboarding })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

/**
 * Sets the visibility of the Product Tour
 * @param {Boolean} showProductTour - set visibility to true/false
 */
function SET_SHOW_PRODUCT_TOUR(state, showProductTour) {
  Vue.set(state, 'showProductTour', showProductTour);
}

/**
 * @description Adds whether blank onboarding page was dismissed
 * @param {Boolean} simplifiedOnboardingDismissed - A boolean tracking blank
 * onboarding page dismissed
 */
function SET_SIMPLIFIED_ONBOARDING_DISMISSED(state, simplifiedOnboardingDismissed) {
  const newGrowthData = (
    updateGrowthDataOnboardingState(state, { simplifiedOnboardingDismissed })
  );
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

function SET_TASK_URL(state, { taskType, url }) {
  implementationChecklistTasksUrls.set(taskType, url);
  Vue.set(state, 'tasksUrls', implementationChecklistTasksUrls);
}

/**
 * Updates the feature/header of an implementation checklist with correct values
 * @param {Object} feature - the feature of the checklist
 */
function UPDATE_IMPLEMENTATION_CHECKLIST(state, { feature }) {
  const currentChecklists = get(state, 'adminGrowthData.onboardingState.implementationChecklists', []);
  const index = currentChecklists.findIndex(checklist => feature.name === checklist.feature);

  const checklist = implementationChecklists[feature.value];
  currentChecklists[index].headerText = checklist.headerText;
  currentChecklists[index].feature = checklist.feature;

  const newGrowthData = (
    updateGrowthDataOnboardingState(state, {
      implementationChecklists: currentChecklists,
    })
  );

  putAdminGrowthData({ growthData: newGrowthData });
  Vue.set(state, 'adminGrowthData', newGrowthData);
}

// Export for unit tests
export const createOnboardingModule = () => ({
  namespaced: true,
  state: {
    // original onboarding tracking
    onboardingTasks: {},
    taskChecklistComplete: false,
    tasksUrls: implementationChecklistTasksUrls,

    // admin level growthData with onboarding tracking
    adminGrowthData: defaultAdminGrowthData,

    // Checklist State
    showChecklistLoading: false,
    initalCheckListFeatures: [],

    // Product Tour State
    showProductTour: false,
    productTourStepIndex: 0,
  },
  actions: {
    addFeatureToImplementationChecklist,
    addFeatureToRecommendedChecklist,
    addImplementationChecklists,
    addOnboardingTasks,
    clearNewFeatureAlert,
    completeOnboardingChecklist,
    completeOnboardingTask,
    removeFeatureFromImplementationChecklist,
    setAdminGrowthData,
    setInitialChecklistFeatures,
    setProductTourStep,
    setShowChecklistLoading,
    setShowOnboarding,
    setShowProductTour,
    updateChatSeen,
    updateChecklistExpandedIndex,
    updateDirectOnboardingFeature,
    updateImplementationChecklist,
    updateImplementationChecklistDismissed,
    updateImplementationTaskStatus,
    updateImplementationTaskStatusAuto,
    updateNewFeatureItemCount,
    updateOnboardingChecklistStatus,
    updateOnboardingFlowComplete,
    updateOnboardingModalSeen,
    updateResourcesDisplayPreferences,
    updateSimplifiedOnboardingDismissed,
    updateTaskUrl,
  },
  getters: {
    getChecklistExpandedIndex,
    getDirectOnboardingFeature,
    getImplementationChecklist,
    getImplementationChecklistDismissed,
    getImplementationChecklistFeaturesComplete,
    getImplementationChecklistTotal,
    getImplementationChecklistTotalComplete,
    getOnboardingFlowComplete,
    getProductTourActiveLink,
    getResourcesDisplayPreferences,
    getResourceFromDisplayPreferences,
    getResourceTypeFilterFromDisplayPreferences,
    getShowOnboarding,
  },
  mutations: {
    ADD_FEATURE_TO_IMPLEMENTATION_CHECKLIST,
    ADD_FEATURE_TO_RECOMMENDED_CHECKLIST,
    CLEAR_NEW_FEATURE_ALERT,
    REMOVE_FEATURE_FROM_IMPLEMENTATION_CHECKLIST,
    SET_ADMIN_GROWTH_DATA,
    SET_CHAT_SEEN,
    SET_CHECKLIST_COMPLETED_TASK,
    SET_CHECKLIST_EXPANDED_INDEX,
    SET_DIRECT_ONBOARDING_FEATURE,
    SET_IMPLEMENTATION_CHECKLIST_DISMISSED,
    SET_IMPLEMENTATION_CHECKLIST_TASK_STATUS,
    SET_IMPLEMENTATION_CHECKLISTS,
    SET_INITIAL_CHECKLIST_FEATURES,
    SET_NEW_FEATURE_ITEM_COUNT,
    SET_ONBOARDING_CHECKLIST_COMPLETE,
    SET_ONBOARDING_MODAL_SEEN,
    SET_ONBOARDING_TASK_STATUS,
    SET_PRODUCT_TOUR_STEP,
    SET_RESOURCES_VISIBILITY,
    SET_SHOW_CHECKLIST_LOADING,
    SET_SHOW_ONBOARDING,
    SET_SHOW_PRODUCT_TOUR,
    SET_SIMPLIFIED_ONBOARDING_DISMISSED,
    SET_TASK_URL,
    UPDATE_IMPLEMENTATION_CHECKLIST,
  },
});

export default createOnboardingModule();
